import { useMemo, useState } from 'react';

import { Button, FormInput, Modal } from '@/components/UI';
import { useAuth } from '@/context/AuthContext';
import { getSupportWriteScopeLabel } from '@/utils/supportWriteScopes';

const formatSupportSessionTime = (value: string) =>
    new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });

const SupportSessionBanner = () => {
    const {
        actorUser,
        extendSupportSession,
        isImpersonating,
        registerSupportPasskey,
        stopSupportSession,
        supportSession,
        supportStepUp,
        user
    } = useAuth();

    const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [isExtending, setIsExtending] = useState(false);
    const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
    const [isStopping, setIsStopping] = useState(false);

    const expiryLabel = useMemo(() => {
        if (!supportSession) {
            return null;
        }

        return formatSupportSessionTime(supportSession.expiresDateTime);
    }, [supportSession]);

    const absoluteExpiryLabel = useMemo(() => {
        if (!supportSession) {
            return null;
        }

        return formatSupportSessionTime(supportSession.absoluteExpiresDateTime);
    }, [supportSession]);

    const activeWriteScopeLabels = useMemo(() => {
        if (!supportSession) {
            return [];
        }

        return supportSession.allowedWriteScopes.map(getSupportWriteScopeLabel);
    }, [supportSession]);

    const isPasskeyStepUp = supportStepUp?.requiredMethod === 'passkey';
    const passkeyCtaLabel = supportStepUp?.hasPasskey ? 'Register Another Passkey' : 'Register Passkey';

    if (!isImpersonating || !supportSession || !user || !actorUser) {
        return null;
    }

    const handleExtend = async () => {
        if (!isPasskeyStepUp && !/^\d{6}$/.test(totpCode)) {
            return;
        }

        try {
            setIsExtending(true);

            try {
                await extendSupportSession(isPasskeyStepUp ? undefined : { totpCode });
            } catch {
                return;
            }

            setTotpCode('');
            setIsExtendModalOpen(false);
        } finally {
            setIsExtending(false);
        }
    };

    const handleStop = async () => {
        try {
            setIsStopping(true);
            await stopSupportSession();
        } finally {
            setIsStopping(false);
        }
    };

    const handleRegisterPasskey = async () => {
        try {
            setIsRegisteringPasskey(true);
            await registerSupportPasskey();
        } catch {
            // Registration failures are surfaced by registerSupportPasskey itself; nothing to do here.
        } finally {
            setIsRegisteringPasskey(false);
        }
    };

    const footer = (
        <>
            <Button disabled={isExtending} variant="secondary" onClick={() => setIsExtendModalOpen(false)}>
                Cancel
            </Button>
            <Button
                disabled={!isPasskeyStepUp && !/^\d{6}$/.test(totpCode)}
                isLoading={isExtending}
                onClick={() => {
                    void handleExtend();
                }}
            >
                Extend Session
            </Button>
        </>
    );

    return (
        <>
            <div
                className={`border-b ${
                    supportSession.mode === 'limitedWrite'
                        ? 'border-rose-500/30 bg-[linear-gradient(90deg,rgba(127,29,29,0.92),rgba(82,20,20,0.95),rgba(15,15,15,0.98))]'
                        : 'border-amber-500/25 bg-[linear-gradient(90deg,rgba(120,53,15,0.88),rgba(64,33,12,0.94),rgba(15,15,15,0.98))]'
                }`}
            >
                <div className="mx-auto flex w-full max-w-[2200px] flex-col gap-4 px-4 py-3 text-white lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                        <div
                            className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${
                                supportSession.mode === 'limitedWrite' ? 'text-rose-200' : 'text-amber-200'
                            }`}
                        >
                            Support Session Active
                        </div>
                        <div className="mt-1 text-sm font-medium text-white">
                            Viewing{' '}
                            <span className={supportSession.mode === 'limitedWrite' ? 'text-rose-100' : 'text-amber-100'}>
                                {user.email}
                            </span>{' '}
                            as{' '}
                            <span className={supportSession.mode === 'limitedWrite' ? 'text-rose-100' : 'text-amber-100'}>
                                {actorUser.email}
                            </span>
                        </div>
                        <div
                            className={`mt-1 text-sm ${supportSession.mode === 'limitedWrite' ? 'text-rose-100/90' : 'text-amber-100/85'}`}
                        >
                            {supportSession.mode === 'limitedWrite'
                                ? `Limited-write access for ${activeWriteScopeLabels.join(', ')}.`
                                : 'Read-only access.'}{' '}
                            Session expires {expiryLabel}; hard expiry {absoluteExpiryLabel}.
                        </div>
                        <div className="mt-1 text-xs text-white/70">
                            Step-up: {supportSession.stepUpMethod === 'passkey' ? 'Passkey' : 'TOTP'}
                        </div>
                        {supportSession.ticketReference && (
                            <div className="mt-1 text-xs text-white/70">Ticket: {supportSession.ticketReference}</div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            disabled={isExtending || isStopping}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => setIsExtendModalOpen(true)}
                        >
                            Extend
                        </Button>
                        <Button
                            disabled={isStopping}
                            isLoading={isStopping}
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                                void handleStop();
                            }}
                        >
                            Stop Impersonating
                        </Button>
                    </div>
                </div>
            </div>

            <Modal footer={footer} isOpen={isExtendModalOpen} title="Extend Support Session" onClose={() => setIsExtendModalOpen(false)}>
                <div className="space-y-4">
                    <p className="text-sm leading-6 text-zinc-400">
                        {isPasskeyStepUp
                            ? 'Approve a fresh passkey check to extend this support session. The active mode and granted scopes do not change.'
                            : 'Enter a fresh admin TOTP code to extend this support session. The active mode and granted scopes do not change.'}
                    </p>
                    {isPasskeyStepUp ? (
                        <div className="space-y-3">
                            <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm leading-6 text-emerald-100">
                                The browser will prompt for passkey approval when you continue.
                            </div>
                            <div className="flex flex-col gap-3 rounded-lg border border-sky-500/20 bg-sky-500/10 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                <div className="text-sm leading-6 text-sky-100">
                                    Use this if this browser or device does not already have one of your registered passkeys available.
                                </div>
                                <Button
                                    disabled={isExtending || isStopping}
                                    isLoading={isRegisteringPasskey}
                                    size="sm"
                                    variant="outline-dark"
                                    onClick={() => {
                                        void handleRegisterPasskey();
                                    }}
                                >
                                    {passkeyCtaLabel}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <FormInput
                            autoComplete="one-time-code"
                            label="Admin TOTP Code"
                            maxLength={6}
                            placeholder="123456"
                            value={totpCode}
                            onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && /^\d{6}$/.test(totpCode)) {
                                    event.preventDefault();
                                    void handleExtend();
                                }
                            }}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};

export default SupportSessionBanner;
